/* ============================================================================
   MODE PROJECTION — plein écran, deux présentations du schéma
   ----------------------------------------------------------------------------
   « Diaporama » (variante 2) : la scène est découpée par la CHRONOLOGIE des
   actions (champ `order`) et présentée comme une succession de vues :
     - étape 0            : « Mise en place » (joueurs, zones, repères — sans action) ;
     - étape k (order o)  : CUMULATIF ATTÉNUÉ — toutes les actions d'order < o
       restent visibles mais atténuées (muted, comme en mode simple), celles
       d'order = o sont pleines. Les actions d'un même `order` forment UN temps
       (elles apparaissent ensemble).
     - les actions SANS `order` forment un dernier temps (« Suite »).
   Les vues du diaporama sont DÉRIVÉES : la scène source n'est jamais modifiée.

   « Schéma complet » (variante 1) : la scène entière (toutes les positions,
   fantômes et actions) est affichée et MODIFIABLE EN DIRECT — déplacer
   joueurs / fantômes / flèches, tracer, supprimer, renuméroter — via le même
   moteur d'édition que l'éditeur principal (editor.js). Les modifications
   portent sur la scène RÉELLE (lastJson) : elles sont conservées à la
   fermeture et enregistrées avec le schéma.

   Deux parties :
     1. Fonctions PURES (buildProjectionSteps / sceneForStep) — testables Node ;
     2. createProjection(...) — contrôleur de l'overlay #projection (DOM).
   ========================================================================== */
import { renderToCanvas } from "./render.js";
import { createEditor } from "./editor.js";

/* Ordre de tri d'une action : les actions sans `order` passent en dernier
   (même convention que renderActions, qui les trie à 9999). */
const NO_ORDER = Number.POSITIVE_INFINITY;
const orderKey = (a) => (a.order == null ? NO_ORDER : Number(a.order));

/* --------------------------------------------------------------------------
   1) LOGIQUE PURE (diaporama)
   -------------------------------------------------------------------------- */

/* Construit la liste des étapes du diaporama pour une scène.
   Retourne toujours AU MOINS l'étape « Mise en place » (scène sans action). */
export function buildProjectionSteps(scene) {
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const keys = [...new Set(actions.map(orderKey))].sort((x, y) => x - y);
  const steps = [{ key: null, label: "Mise en place" }];
  keys.forEach((k) => {
    const group = actions.filter((a) => orderKey(a) === k);
    // Étiquette : les labels des actions du temps (dédupliqués), sinon « Temps N ».
    const labels = [...new Set(group.map((a) => (a.label || "").trim()).filter(Boolean))];
    const fallback = k === NO_ORDER ? "Suite" : `Temps ${k}`;
    steps.push({ key: k, label: labels.length ? labels.slice(0, 3).join(" · ") : fallback });
  });
  return steps;
}

/* Vue de la scène à l'étape `index` (0 = mise en place).
   Cumulatif atténué : actions passées -> muted, temps courant -> plein,
   actions futures -> absentes. Copies superficielles : la source est intacte. */
export function sceneForStep(scene, steps, index) {
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const step = steps[Math.max(0, Math.min(index, steps.length - 1))];
  if (!step || step.key == null) return { ...scene, actions: [] };
  const view = actions
    .filter((a) => orderKey(a) <= step.key)
    .map((a) => (orderKey(a) < step.key ? { ...a, muted: true } : { ...a, muted: false }));
  return { ...scene, actions: view };
}

/* Vue ANIMÉE de l'étape : les actions du temps COURANT portent `animT` (0..1)
   — le moteur de rendu les dessine partiellement (la pointe glisse du départ
   vers la cible). t >= 1 (ou étape « mise en place ») : vue normale. */
export function sceneForStepAnimated(scene, steps, index, t) {
  const view = sceneForStep(scene, steps, index);
  const step = steps[Math.max(0, Math.min(index, steps.length - 1))];
  if (!step || step.key == null || !(t < 1)) return view;
  return { ...view, actions: view.actions.map((a) => (a.muted ? a : { ...a, animT: t })) };
}

/* --------------------------------------------------------------------------
   2) CONTRÔLEUR DE L'OVERLAY (DOM)
   --------------------------------------------------------------------------
   L'overlay est une simple vue fixe qui couvre la page (compatible partout,
   y compris iOS Safari). Le plein écran NATIF est tenté en bonus quand le
   navigateur le permet (échec silencieux sinon).
   Rendu :
     - Diaporama : renderToCanvas avec un facteur d'échelle (netteté sur
       grand écran), état du canvas principal préservé ;
     - Schéma complet : renderToCanvas en géométrie native (scale 1) avec
       opts.track — la géométrie et les segments publiés permettent à
       l'éditeur (createEditor) de travailler sur CE canvas.
   -------------------------------------------------------------------------- */
const REF_W = 1000, REF_H = 700;   // géométrie de référence du schéma (paysage)

export function createProjection({ getScene, getSport, getMode, onSceneEdited, onClose }) {
  const $ = (id) => document.getElementById(id);
  const root = $("projection"), stage = $("projStage"), cnv = $("projCanvas");
  const elCounter = $("projCounter"), elLabel = $("projLabel"), elTitle = $("projTitle");
  const btnPrev = $("projPrev"), btnNext = $("projNext"), btnClose = $("projClose"), btnFs = $("projFullscreen");
  const elNav = $("projNav"), elEditBar = $("projEditBar"), elHint = $("projHint");
  const btnToolSelect = $("projToolSelect"), btnToolArrow = $("projToolArrow");
  const selArrowType = $("projArrowType"), btnDelete = $("projDeleteSel");
  const modeSeg = $("projModeSeg");

  const HINT_DEFAULT = "Glisse un joueur, un fantôme ou une flèche · poignées = orienter · double-clic = renommer · Suppr = supprimer.";

  let steps = [], idx = 0, isOpen = false;
  let pmode = "slides";   // "slides" (diaporama) | "edit" (schéma complet modifiable)
  let dirty = false;      // des modifications ont eu lieu pendant la projection

  /* Éditeur dédié au canvas de projection : mêmes gestes que l'éditeur
     principal. Il est INERTE hors mode « Schéma complet » (getScene -> null). */
  const editing = () => isOpen && pmode === "edit";
  const editor = createEditor(cnv, {
    getScene: () => (editing() ? getScene() : null),
    getView: () => (editing() ? getScene() : null),
    repaint: () => { if (isOpen) paint(); },
    onChange: () => { dirty = true; onSceneEdited && onSceneEdited(); },
    setHint: (m) => { elHint.textContent = m || HINT_DEFAULT; },
    onSelectionChange: (sel) => {
      btnDelete.disabled = !sel;
      if (sel && sel.kind === "action" && sel.ref.type) selArrowType.value = sel.ref.type;
    },
    onToolChange: (t) => {
      btnToolSelect.classList.toggle("is-active", t === "select");
      btnToolArrow.classList.toggle("is-active", t === "arrow");
    },
    isEditing: editing,
  });

  /* Plein écran natif : bonus si disponible (indisponible sur iPhone : le
     bouton est masqué, l'overlay suffit). */
  const fsRequest = root.requestFullscreen || root.webkitRequestFullscreen;
  const fsExit = () => (document.exitFullscreen || document.webkitExitFullscreen || (() => {})).call(document);
  const fsElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;
  if (!fsRequest) btnFs.hidden = true;
  function enterFullscreen() {
    if (!fsRequest) return;
    try { const p = fsRequest.call(root); if (p && p.catch) p.catch(() => {}); } catch { /* refusé : l'overlay reste */ }
  }

  /* Adapte la taille CSS du canvas à l'espace disponible (ratio 10:7 conservé). */
  function fit() {
    const availW = Math.max(100, stage.clientWidth - 24);
    const availH = Math.max(100, stage.clientHeight - 24);
    const w = Math.min(availW, availH * (REF_W / REF_H));
    cnv.style.width = Math.round(w) + "px";
    cnv.style.height = Math.round(w * (REF_H / REF_W)) + "px";
  }

  function paint(animT) {
    const scene = getScene();
    if (!scene) return;
    elTitle.textContent = scene.title || "";
    if (pmode === "edit") {
      // Géométrie native + suivi : le canvas devient la cible de l'éditeur.
      cnv.width = REF_W; cnv.height = REF_H;
      renderToCanvas(cnv, scene, getSport(), getMode(), { track: true, selection: editor.getSelection() });
      return;
    }
    // Diaporama : résolution interne = taille affichée × densité de pixels (bornée).
    const cssW = parseFloat(cnv.style.width) || REF_W;
    const scale = Math.min(3, Math.max(1, (cssW / REF_W) * (window.devicePixelRatio || 1)));
    cnv.width = Math.round(REF_W * scale);
    cnv.height = Math.round(REF_H * scale);
    const view = animT != null ? sceneForStepAnimated(scene, steps, idx, animT) : sceneForStep(scene, steps, idx);
    renderToCanvas(cnv, view, getSport(), getMode(), { scale });
    elCounter.textContent = `${idx + 1} / ${steps.length}`;
    elLabel.textContent = steps[idx] ? steps[idx].label : "";
    btnPrev.disabled = idx <= 0;
    btnNext.disabled = idx >= steps.length - 1;
  }

  function setPMode(m) {
    if (pmode === m) return;
    pmode = m;
    stopAnim();
    if (m !== "edit") {
      editor.clearSelection();
      // Les actions ont pu changer pendant l'édition : le découpage est refait
      // (l'étape courante est bornée au nouveau nombre de temps).
      steps = buildProjectionSteps(getScene());
      idx = Math.min(idx, steps.length - 1);
    } else {
      editor.setTool("select");
      elHint.textContent = HINT_DEFAULT;
    }
    syncModeUi();
    paint();
  }
  function syncModeUi() {
    const edit = pmode === "edit";
    root.classList.toggle("projection--edit", edit);
    elNav.hidden = edit;
    elEditBar.hidden = !edit;
    modeSeg.querySelectorAll("button").forEach((b) =>
      b.classList.toggle("is-active", b.getAttribute("data-pmode") === pmode));
  }

  /* Passage d'étape ANIMÉ (avance d'un temps) : les actions du nouveau temps
     se déroulent (~750 ms, sortie douce) — la passe file, la course s'étire,
     le fantôme glisse jusqu'à sa position. Recul, saut ou préférence système
     « réduire les animations » : affichage direct. */
  const reducedMotion = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let animFrame = 0;
  const stopAnim = () => { if (animFrame) { cancelAnimationFrame(animFrame); animFrame = 0; } };
  function goTo(i) {
    const next = Math.max(0, Math.min(i, steps.length - 1));
    if (next === idx) return;
    const forward = next === idx + 1;
    idx = next;
    stopAnim();
    if (!forward || pmode !== "slides" || reducedMotion()) { paint(); return; }
    const t0 = performance.now(), DUR = 750;
    const frame = (now) => {
      animFrame = 0;
      if (!isOpen || pmode !== "slides") { paint(); return; }
      const raw = Math.min(1, (now - t0) / DUR);
      const t = 1 - Math.pow(1 - raw, 3);   // easeOutCubic : rapide puis amorti
      paint(raw < 1 ? t : undefined);
      if (raw < 1) animFrame = requestAnimationFrame(frame);
    };
    animFrame = requestAnimationFrame(frame);
  }

  function onKey(e) {
    if (!isOpen) return;
    const k = e.key;
    // Champs de saisie éventuels (window.prompt est modal, mais restons sûrs).
    const tag = (document.activeElement && document.activeElement.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return;

    if (k === "f" || k === "F") { e.preventDefault(); fsElement() ? fsExit() : enterFullscreen(); return; }
    if (k === "Escape") {
      // En plein écran natif, Échap est déjà consommé par le navigateur pour
      // en sortir : on ne ferme l'overlay qu'au 2e Échap.
      if (fsElement()) return;
      close(); return;
    }
    if (pmode === "edit") {
      if (k === "Delete" || k === "Backspace") { e.preventDefault(); editor.deleteSelection(); }
      return;   // pas de navigation d'étapes en mode édition
    }
    if (k === "ArrowRight" || k === "ArrowDown" || k === "PageDown" || k === " " || k === "Enter") { e.preventDefault(); goTo(idx + 1); }
    else if (k === "ArrowLeft" || k === "ArrowUp" || k === "PageUp" || k === "Backspace") { e.preventDefault(); goTo(idx - 1); }
    else if (k === "Home") { e.preventDefault(); goTo(0); }
    else if (k === "End") { e.preventDefault(); goTo(steps.length - 1); }
  }
  function onResize() { if (isOpen) { fit(); paint(); } }

  /* Navigation façon diaporama : clic à droite du schéma = suivant,
     à gauche = précédent (en plus des boutons et du clavier).
     Inactif en mode édition (les gestes appartiennent à l'éditeur). */
  function onStageClick(e) {
    if (pmode === "edit") return;
    const r = stage.getBoundingClientRect();
    (e.clientX - r.left < r.width * 0.35) ? goTo(idx - 1) : goTo(idx + 1);
  }

  function open() {
    const scene = getScene();
    if (!scene) return;
    steps = buildProjectionSteps(scene);
    idx = 0;
    dirty = false;
    isOpen = true;
    // Types de flèches alignés sur le sport courant : on clone la liste de
    // l'éditeur principal (mise à jour par updateArrowTypeOptions).
    const mainSel = document.getElementById("arrowType");
    if (mainSel) { selArrowType.innerHTML = mainSel.innerHTML; editor.setActionType(selArrowType.value); }
    root.hidden = false;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onResize);
    syncModeUi();
    enterFullscreen();   // appelé depuis le clic utilisateur : geste valide
    fit(); paint();
  }

  function close() {
    isOpen = false;
    stopAnim();
    editor.clearSelection();
    root.hidden = true;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onResize);
    document.removeEventListener("fullscreenchange", onResize);
    if (fsElement()) fsExit();
    // Resynchronise l'application (canvas principal, compteurs) : les
    // modifications faites en projection portent sur la scène réelle.
    onClose && onClose(dirty);
    dirty = false;
  }

  btnPrev.addEventListener("click", () => goTo(idx - 1));
  btnNext.addEventListener("click", () => goTo(idx + 1));
  btnClose.addEventListener("click", close);
  btnFs.addEventListener("click", () => (fsElement() ? fsExit() : enterFullscreen()));
  stage.addEventListener("click", onStageClick);
  modeSeg.querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => setPMode(b.getAttribute("data-pmode"))));
  btnToolSelect.addEventListener("click", () => editor.setTool("select"));
  btnToolArrow.addEventListener("click", () => editor.setTool("arrow"));
  selArrowType.addEventListener("change", (e) => editor.setActionType(e.target.value));
  btnDelete.addEventListener("click", () => editor.deleteSelection());

  return { open, close, isOpen: () => isOpen };
}
